import { injectable } from 'tsyringe';
import { authenticatedFetch } from '@/shared/auth/AuthenticatedFetch';

/** Per-document acceptance state for the current legal bundle. */
export interface LegalDocumentStatus {
    type: string;
    title: string;
    summary: string;
    accepted: boolean;
    acceptedAt?: string;
    acceptedBy?: string;
}

/** The tenant's legal-acceptance posture plus the caller's gate flag (GL-42). */
export interface LegalStatus {
    currentVersion: string;
    accepted: boolean;
    needsAcceptance: boolean;
    documents: LegalDocumentStatus[];
    acceptedAt?: string;
}

/**
 * Reads and records tenant acceptance of the ToS/DPA bundle (GL-42), over the
 * auth BFF path. Mirrors {@link OnboardingService}'s raw-fetch approach — no
 * generated api-client, so a legal-copy change never triggers a client cascade.
 */
@injectable()
export class LegalService {
    async getStatus(): Promise<LegalStatus> {
        const res = await authenticatedFetch('/_auth/api/legal/status');
        if (!res.ok) throw new Error(`Failed to load legal status (${res.status})`);
        return (await res.json()) as LegalStatus;
    }

    async accept(): Promise<LegalStatus> {
        const res = await authenticatedFetch('/_auth/api/legal/accept', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: '{}',
        });
        if (!res.ok) throw new Error(`Failed to record acceptance (${res.status})`);
        return (await res.json()) as LegalStatus;
    }
}
